import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';

import { CctMembroResponse } from '@/core/api/sicom-api/comissoes/cadastros/comissao-cadastro.model';
import { ReuniaoComissao } from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { SendWhatsapp } from '@/shared/components/send-whatsapp/send-whatsapp';

@Component({
    selector: 'app-composicao-membros-comissoes',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, CardContainer, TooltipModule, SendWhatsapp],
    templateUrl: './composicao-membros-comissoes.html',
    styleUrls: ['./composicao-membros-comissoes.scss']
})
/**
 * Card de composição da comissão: lista membros (ativos/afastados) com contatos,
 * cópia de e-mail/telefone e geração de mensagem de WhatsApp por membro.
 */
export class ComposicaoMembrosComissoesComponent {
    private readonly messageService = inject(MessageService, { optional: true });

    /** Membros a exibir. */
    readonly membros = input<CctMembroResponse[]>([]);
    /** Exibe skeletons enquanto carrega. */
    readonly carregando = input(false);
    /** Título do card. */
    readonly titulo = input('Composição atual');
    /** Subtítulo (nome da comissão/CT). */
    readonly subtitulo = input('Integrantes ativos e afastados');
    /** Reunião de contexto (usada na mensagem de WhatsApp). */
    readonly reuniao = input<ReuniaoComissao | null>(null);

    membrosExpandidos = new Set<string>();

    toggleExpandMembro(event: Event, membro: CctMembroResponse): void {
        event.stopPropagation();
        const chave = this.chaveMembro(membro);

        if (this.membrosExpandidos.has(chave)) {
            this.membrosExpandidos.delete(chave);
            return;
        }

        this.membrosExpandidos.add(chave);
    }

    isMembroExpanded(membro: CctMembroResponse): boolean {
        return this.membrosExpandidos.has(this.chaveMembro(membro));
    }

    obterClasseParticipacao(item: CctMembroResponse): string {
        const desc = (item?.dsParticipacao || '').trim().toUpperCase();

        if (desc.includes('COORDENADOR') && !desc.includes('VICE')) {
            return 'participacao-coordenador';
        }

        if (desc.includes('VICE')) {
            return 'participacao-vice';
        }

        if (desc.includes('EFETIVO') && desc.includes('INTERNO')) {
            return 'participacao-efetivo-interno';
        }

        if (desc.includes('EFETIVO') && desc.includes('EXTERNO')) {
            return 'participacao-efetivo-externo';
        }

        if (desc.includes('EFETIVO')) {
            return 'participacao-efetivo';
        }

        if (desc.includes('AFASTADO')) {
            return 'participacao-afastado';
        }

        if (desc.includes('MEMBRO')) {
            return 'participacao-membro';
        }

        if (desc.includes('SECRET')) {
            return 'participacao-secretario';
        }

        if (desc.includes('SUPLENTE')) {
            return 'participacao-suplente';
        }

        return 'participacao-padrao';
    }

    iniciaisMembro(nome: string): string {
        if (!nome) {
            return '';
        }

        const partes = nome.trim().split(/\s+/);
        if (!partes.length) {
            return '';
        }

        if (partes.length === 1) {
            return partes[0].charAt(0).toUpperCase();
        }

        return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
    }

    obterListaEmails(emails?: string): string[] {
        if (!emails) {
            return [];
        }

        return emails
            .split(/[,;\s]+/)
            .map((email) => email.trim())
            .filter(Boolean);
    }

    obterListaTelefones(telefones?: string): string[] {
        if (!telefones) {
            return [];
        }

        return telefones
            .split(/[/,;]+/)
            .map((telefone) => telefone.trim())
            .filter(Boolean);
    }

    async copiarEmail(email: string, event: Event): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        const texto = email?.trim();
        if (!texto) {
            this.messageService?.add({ severity: 'warn', summary: 'Aviso', detail: 'Nenhum e-mail disponível para copiar.' });
            return;
        }

        try {
            await this.copiarParaAreaTransferencia(texto);
            this.messageService?.add({ severity: 'success', summary: 'Copiado', detail: 'E-mail copiado para a área de transferência.' });
        } catch {
            this.messageService?.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível copiar o e-mail.' });
        }
    }

    async copiarEmailsMembro(emails: string | undefined, event: Event): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        const lista = this.obterListaEmails(emails);
        if (!lista.length) {
            this.messageService?.add({ severity: 'warn', summary: 'Aviso', detail: 'Nenhum e-mail disponível para copiar.' });
            return;
        }

        try {
            await this.copiarParaAreaTransferencia(lista.join('; '));
            this.messageService?.add({ severity: 'success', summary: 'Copiado', detail: 'E-mails do membro copiados para a área de transferência.' });
        } catch {
            this.messageService?.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível copiar os e-mails do membro.' });
        }
    }

    async copiarTelefone(telefone: string, event: Event): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        const texto = telefone?.trim();
        if (!texto) {
            this.messageService?.add({ severity: 'warn', summary: 'Aviso', detail: 'Nenhum telefone disponível para copiar.' });
            return;
        }

        try {
            await this.copiarParaAreaTransferencia(texto);
            this.messageService?.add({ severity: 'success', summary: 'Copiado', detail: 'Telefone copiado para a área de transferência.' });
        } catch {
            this.messageService?.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível copiar o telefone.' });
        }
    }

    async copiarTelefonesMembro(telefones: string | undefined, event: Event): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        const lista = this.obterListaTelefones(telefones);
        if (!lista.length) {
            this.messageService?.add({ severity: 'warn', summary: 'Aviso', detail: 'Nenhum telefone disponível para copiar.' });
            return;
        }

        try {
            await this.copiarParaAreaTransferencia(lista.join('; '));
            this.messageService?.add({ severity: 'success', summary: 'Copiado', detail: 'Telefones do membro copiados para a área de transferência.' });
        } catch {
            this.messageService?.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível copiar os telefones do membro.' });
        }
    }

    possuiEmails(emails?: string): boolean {
        return this.obterListaEmails(emails).length > 0;
    }

    possuiTelefones(telefones?: string): boolean {
        return this.obterListaTelefones(telefones).length > 0;
    }

    gerarMensagemWhatsappMembro(membro: CctMembroResponse): string {
        const reuniao = this.reuniao();
        const linhas = [
            '\u{1F465} *Contato de comissao*',
            membro.nome ? `\u{1F464} Nome: ${membro.nome}` : '',
            this.subtitulo() ? `\u{1F3DB}\uFE0F Comissao/CT: ${this.subtitulo()}` : '',
            membro.dsParticipacao ? `\u{1F3F7}\uFE0F Participacao: ${membro.dsParticipacao}` : ''
        ];

        if (reuniao) {
            linhas.push(
                reuniao.codigo ? `\u{1F522} Reuniao: ${reuniao.codigo}` : '',
                reuniao.dataInicio ? `\u{1F4C5} Data e horario: ${this.formatarDataHoraMensagem(reuniao.dataInicio)}` : '',
                reuniao.dataInicio && reuniao.dataTermino ? `\u{1F552} Horario: ${this.formatarPeriodoMensagem(reuniao)}` : '',
                reuniao.local ? `\u{1F4CD} Local: ${reuniao.local}` : '',
                reuniao.modalidade ? `\u{1F4BB} Modalidade: ${reuniao.modalidade}` : '',
                reuniao.protocolo ? `\u{1F9FE} Protocolo: ${reuniao.protocolo}` : ''
            );
        }

        return linhas.filter((linha) => linha !== '').join('\n');
    }

    private formatarDataHoraMensagem(data: Date | null): string {
        if (!data) {
            return '-';
        }

        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(data);
    }

    private formatarPeriodoMensagem(reuniao: ReuniaoComissao): string {
        if (!reuniao.dataInicio || !reuniao.dataTermino) {
            return '-';
        }

        const hora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
        return `${hora.format(reuniao.dataInicio)} - ${hora.format(reuniao.dataTermino)}`;
    }

    gerarMensagemWhatsappMembroDashboard(membro: CctMembroResponse): string {
        const reuniao = this.reuniao();
        const nomeComissao = reuniao?.nomeComissao || this.subtitulo();
        const linhas = [
            '\u{1F465} *Contato de comissao*',
            membro.nome ? `\u{1F464} Nome: ${membro.nome}` : '',
            nomeComissao ? `\u{1F3DB}\uFE0F Comissao/CT: ${nomeComissao}` : '',
            membro.dsParticipacao ? `\u{1F3F7}\uFE0F Participacao: ${membro.dsParticipacao}` : '',
            reuniao?.codigo ? `\u{1F522} Reuniao: ${reuniao.codigo}` : '',
            reuniao?.dataInicio ? `\u{1F4C5} Data e horario: ${this.formatarDataHoraMensagem(reuniao.dataInicio)}` : '',
            reuniao?.dataInicio && reuniao?.dataTermino ? `\u{1F552} Horario: ${this.formatarPeriodoMensagem(reuniao)}` : '',
            reuniao?.local ? `\u{1F4CD} Local: ${reuniao.local}` : '',
            reuniao?.modalidade ? `\u{1F4BB} Modalidade: ${reuniao.modalidade}` : '',
            reuniao?.protocolo ? `\u{1F9FE} Protocolo: ${reuniao.protocolo}` : ''
        ];

        return linhas.filter((linha) => linha !== '').join('\n');
    }

    private async copiarParaAreaTransferencia(texto: string): Promise<void> {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(texto);
            return;
        }

        const textarea = document.createElement('textarea');
        textarea.value = texto;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const copiado = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!copiado) {
            throw new Error('Clipboard indisponível');
        }
    }

    formatarDataExibicao(data?: string | null): string {
        if (!data) {
            return 'N/A';
        }

        const partes = data.split(/[-T/]/).filter(Boolean);
        if (partes.length >= 3 && partes[0].length === 4) {
            return `${partes[2].slice(0, 2)}/${partes[1]}/${partes[0]}`;
        }

        return data;
    }

    private chaveMembro(membro: CctMembroResponse): string {
        const id = membro.idSolicitante || membro.idSolicitanteEe || membro.crm || membro.nome || '';
        const uf = membro.uf || membro.ufSgUfEe || '';
        return `${id}-${uf}`;
    }
}
